import {css} from '@emotion/react';
import {faPlus} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Group, Stack, Title} from '@mantine/core';
import {FC, useMemo} from 'react';
import {Link} from 'react-router-dom';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {ReviewStatuses, Roles} from '../../api/generated/enums';
import {ReviewsService} from '../../api/generated/ReviewsService';
import {useRequireAuthorization} from '../../auth/require-authorization';
import {BasicPage} from '../../components/basic-page';
import {BreadcrumbPart} from '../../components/page-title';
import {
  PagedDataTableConfig,
  renderEditButton,
  usePagedDataTable,
} from '../../hooks/paged-data-table/use-paged-data-table';
import {ROUTES} from '../../routes';
import {JobDocumentListing} from '../documents/job-document-listing';
import {useJobStore} from '../job-store';
import {renderedDateFormat} from '../../utils/luxon.ts';
import {useAsync} from 'react-use';
import {AnchorIcon} from '../../components/anchor-icon.tsx';
import {getColorForStatus} from '../job-highlight.tsx';
import {useJobCrumbs} from '../hooks/use-job-crumbs.ts';
import {UsersService} from '../../api/generated/UsersService.ts';

const ResponseRoles = ['Global Admin', 'Partner Site Admin'] as Roles[];

export const ReviewListing: FC = () => {
  const {jobId} = useTypedParams(ROUTES.ADMIN.JOB_REVIEWS.INDEX);
  const isAuthorizedGlobalAdmin = useRequireAuthorization({
    role: 'Global Admin',
  });
  const isAuthorized = useRequireAuthorization({
    role: ResponseRoles,
    permission: 'Review Inbox: Manage',
  });
  const userPartnerSiteIds = useAsync(
    async () =>
      !isAuthorized ? (await UsersService.getPartnerSiteIds()).data : undefined,
    [isAuthorized],
  ).value;

  const {job, isLoading, isJobReadonly, fetchJob} = useJobStore((state) => ({
    job: state.job ?? undefined,
    isLoading: state.isJobLoading,
    isJobReadonly: state.isJobReadonly,
    fetchJob: state.fetchJob,
  }));

  const breadcrumbs = useMemo<BreadcrumbPart[]>(() => {
    const {
      levelOfServiceDescription,
      partNumber,
      assetNumber,
      serialNumber,
      status,
      quoteStatus,
      reviewStatus,
      clientName,
      isRepairable,
    } = useJobCrumbs(job);

    if (isLoading) {
      return [
        {title: 'Jobs', href: ROUTES.ADMIN.JOBS.INDEX.path},
        {
          title: `${jobId}`,
          href: ROUTES.ADMIN.JOBS.DETAIL.buildPath({id: jobId}),
        },
        {title: 'Review Log'},
        {title: 'Loading...', isLoading: true},
      ];
    }

    let crumbs: BreadcrumbPart[] = [
      {title: 'Jobs', href: ROUTES.ADMIN.JOBS.INDEX.path},
      {
        title: `${jobId}`,
        href:
          isAuthorizedGlobalAdmin || userPartnerSiteIds?.some(x => x === job?.partnerSiteId)
            ? ROUTES.ADMIN.JOBS.DETAIL.buildPath({id: jobId})
            : undefined,
      },
      {title: 'Review Log'},
      {
        title: status,
        description: 'Job Status',
        color: getColorForStatus('status', status),
      },
      {title: levelOfServiceDescription, description: 'Level of Service'},
      {title: serialNumber, copiable: true, description: 'Serial Number'},
      {title: partNumber, copiable: true, description: 'Part Number'},
      {title: clientName, description: 'Client Name'},
      {title: assetNumber, description: 'Asset Number'},
      {
        title: isRepairable,
        color: getColorForStatus(
          'evaluationIsRepairable',
          isRepairable.toLowerCase(),
        ),
        description: 'Repairable',
      },
      {
        title: quoteStatus,
        color: getColorForStatus('evaluationQuoteStatus', quoteStatus),
        description: 'Quote Status',
      },
      {
        title: reviewStatus,
        color: getColorForStatus('reviewStatus', reviewStatus),
        description: 'Review Status',
      },
      {
        title: job?.partnerSiteName ?? '',
        description: 'Partner Site',
      },
    ];

    if (job?.isNewRevisionAvailable) {
      crumbs = [
        ...crumbs,
        {
          title: 'New Revision Available',
          color: 'orange',
        },
      ];
    }

    return crumbs;
  }, [job, isLoading, jobId, isAuthorizedGlobalAdmin, userPartnerSiteIds]);

  useAsync(async () => {
    await fetchJob(jobId);
  }, [fetchJob, jobId]);

  const runTimeConfig = useMemo(
    () => ({
      renderActions: isAuthorized ? (
        <Button
          component={Link}
          to={ROUTES.ADMIN.JOB_REVIEWS.RESPONSE.buildPath({jobId})}
        >
          <Group gap={'xs'}>
            <FontAwesomeIcon icon={faPlus} /> New Response
          </Group>
        </Button>
      ) : (
        <>
          {!isJobReadonly && (
            <Button
              component={Link}
              to={ROUTES.ADMIN.JOB_REVIEWS.CREATE.buildPath({jobId})}
            >
              <Group gap={'xs'}>
                <FontAwesomeIcon icon={faPlus} /> New Review
              </Group>
            </Button>
          )}
        </>
      ),
    }),
    [isAuthorized, isJobReadonly, jobId],
  );

  const pagedDataTable = usePagedDataTable(
    ReviewsService.getAllByJobId,
    TableConfig,
    {
      ...runTimeConfig,
      additionalParams: {
        jobId,
      },
    },
  );

  return (
    <BasicPage title={breadcrumbs}>
      {pagedDataTable}
      <Title mt={16} fz='md'>
        Documents
      </Title>
      <JobDocumentListing m='16 0 0 0' id={jobId} review />
    </BasicPage>
  );
};

const getHighlightColor = (value: string | undefined) => {
  if (value === ReviewStatuses['Pending Review'])
    return getColorForStatus(
      'reviewInboxStatus',
      ReviewStatuses['Pending Review'],
    );
  if (value === ReviewStatuses['Review Completed'])
    return getColorForStatus(
      'reviewInboxStatus',
      ReviewStatuses['Review Completed'],
    );
  return getColorForStatus(
    'reviewInboxStatus',
    ReviewStatuses['Request More Information'],
  );
};

const TableConfig = PagedDataTableConfig(ReviewsService.getAllByJobId, {
  columns: [
    {
      title: '',
      accessor: 'copy',
      render: (item) =>
        renderEditButton({
          item: {...item, id: Number(item.id)},
          descriptor: 'review',
          route: ROUTES.ADMIN.JOB_REVIEWS.DETAIL.buildPath({
            jobId: Number(item.jobId),
            id: Number(item.id),
          }),
        }),
      width: 50,
    },
    {
      title: 'Review ID',
      accessor: 'id',
    },
    {
      title: 'Message',
      accessor: 'message',
      width: '20%',
    },
    {
      title: 'Entered By',
      accessor: 'createdByUserLastName',
      render: (item) =>
        `${item.createdByUserFirstName} ${item.createdByUserLastName}`,
      sortable: true,
      width: '20%',
    },
    {
      title: 'Status',
      accessor: 'status',
      cellsStyle: (item) => ({
        backgroundColor: getHighlightColor(item.status),
      }),
      sortable: true,
      width: '20%',
    },
    {
      title: 'Documents',
      accessor: 'documents',
      render: (item) => (
        <Stack css={documentsCss} gap={0}>
          {item.documents &&
            item.documents?.map((x) => (
              <AnchorIcon
                href={x.url}
                target='_blank'
                rel='noopener noreferrer'
              >
                {x.name}
              </AnchorIcon>
            ))}
        </Stack>
      ),
    },
    {
      title: 'Date',
      accessor: 'createdDate',
      render: (item) => renderedDateFormat(item.createdDate),
      sortable: true,
      width: '20%',
    },
  ],
  searchFieldNames: [
    'createdByUserFirstName',
    'createdByUserLastName',
    'status',
    'jobId',
  ],
  defaultSort: {
    column: 'createdDate',
    direction: 'DESC',
  },
  refetchEvent: 'review-log',
});

const documentsCss = css({
  overflowY: 'auto',
  overflowX: 'hidden',
  maxHeight: 56,
});